import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId, publicAnonKey } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import type { Constructor } from './mixins';

export const withAnalytics = <TBase extends Constructor<any>>(Base: TBase) => class Analytics extends Base {
  // ANALYTICS & CALCULATIONS
  // ============================================================================

  /**
   * Calculate cash flow forecasts for a client
   */
  public calculateCashFlowForecast(clientId: string): {
    next_30_days: number;
    next_60_days: number;
    next_90_days: number;
  } {
    const now = new Date();
    const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const sixtyDays = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
    const ninetyDays = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);

    let next30 = 0;
    let next60 = 0;
    let next90 = 0;

    // Get client projects
    const projects = this.getProjects().filter((p: Project) => p.clientId === clientId && p.status === 'active');

    projects.forEach((project: Project) => {
      // Get interim applications
      const apps = this.getInterimApplicationsByProject(project.id);

      apps.forEach((app: InterimApplication) => {
        if (app.status === 'certified' && app.periodEnd) {
          // Payment expected 30 days after period end
          const expectedPayDate = new Date(new Date(app.periodEnd).getTime() + 30 * 24 * 60 * 60 * 1000);
          const unpaid = (app.cumulative_total || 0) - ((app as any).cumulative_paid || 0);

          if (unpaid > 0) {
            if (expectedPayDate <= thirtyDays) {
              next30 += unpaid;
            }
            if (expectedPayDate <= sixtyDays) {
              next60 += unpaid;
            }
            if (expectedPayDate <= ninetyDays) {
              next90 += unpaid;
            }
          }
        }

        // Add draft applications (upcoming valuations)
        if (app.status === 'draft' && app.periodEnd) {
          const valuationDate = new Date(app.periodEnd);
          const expectedPayDate = new Date(valuationDate.getTime() + 30 * 24 * 60 * 60 * 1000);
          const expectedValue = (app as any).this_period_total || (app.cumulative_total || 0) * 0.1; // Estimate 10% of contract value

          if (valuationDate <= thirtyDays && expectedValue > 0) {
            next30 += expectedValue;
          }
          if (valuationDate <= sixtyDays && expectedValue > 0) {
            next60 += expectedValue;
          }
          if (valuationDate <= ninetyDays && expectedValue > 0) {
            next90 += expectedValue;
          }
        }
      });

      // Add retention releases
      if (project.practicalCompletionDate) {
        const pcDate = new Date(project.practicalCompletionDate);
        const firstRetentionRelease = new Date(pcDate.getTime()); // At PC
        const finalRetentionRelease = new Date(pcDate.getTime() + 365 * 24 * 60 * 60 * 1000); // 12 months after PC

        const retentionHeld = apps.reduce((sum: number, app: InterimApplication) => sum + ((app as any).retention_held || 0) - ((app as any).retention_released || 0), 0);
        const halfRetention = retentionHeld / 2;

        if (firstRetentionRelease >= now && firstRetentionRelease <= ninetyDays && halfRetention > 0) {
          if (firstRetentionRelease <= thirtyDays) next30 += halfRetention;
          if (firstRetentionRelease <= sixtyDays) next60 += halfRetention;
          next90 += halfRetention;
        }

        if (finalRetentionRelease >= now && finalRetentionRelease <= ninetyDays && halfRetention > 0) {
          if (finalRetentionRelease <= thirtyDays) next30 += halfRetention;
          if (finalRetentionRelease <= sixtyDays) next60 += halfRetention;
          next90 += halfRetention;
        }
      }
    });

    return {
      next_30_days: Math.round(next30),
      next_60_days: Math.round(next60),
      next_90_days: Math.round(next90),
    };
  }

  /**
   * Calculate commercial health score for a client (0-100)
   */
  public calculateCommercialHealthScore(clientId: string): number {
    const projects = this.getProjects().filter((p: Project) => p.clientId === clientId && p.status === 'active');

    if (projects.length === 0) return 100; // No projects = no issues

    let score = 100;
    let paymentCount = 0;
    let onTimePayments = 0;
    let overdueCount = 0;
    let disputeCount = 0;
    let totalContractValue = 0;
    let certifiedValue = 0;
    const now = new Date();

    projects.forEach((project: Project) => {
      totalContractValue += project.contractValue || 0;

      const apps = this.getInterimApplicationsByProject(project.id);
      const items = this.getContractItemsByProject(project.id);

      // Payment performance
      apps.forEach((app: InterimApplication) => {
        const cumulativePaid: number | undefined = (app as any).cumulative_paid;
        if (cumulativePaid && cumulativePaid > 0) {
          paymentCount++;
          certifiedValue += app.cumulative_total || 0;

          // Check if paid on time (within 35 days of period end)
          if (app.periodEnd) {
            const expectedPayDate = new Date(new Date(app.periodEnd).getTime() + 35 * 24 * 60 * 60 * 1000);
            if (now <= expectedPayDate || cumulativePaid >= (app.cumulative_total ?? 0)) {
              onTimePayments++;
            }
          }
        }

        // Check for overdue payments
        if (app.status === 'certified' && app.periodEnd) {
          const dueDate = new Date(new Date(app.periodEnd).getTime() + 30 * 24 * 60 * 60 * 1000);
          if (dueDate < now) {
            const unpaid = (app.cumulative_total || 0) - ((app as any).cumulative_paid || 0);
            if (unpaid > 0) {
              overdueCount++;
            }
          }
        }
      });

      // Dispute tracking — ContractItem.status doesn't include 'disputed' in its union, but
      // some API paths return it as a runtime value; cast to string for comparison.
      items.forEach((item: ContractItem) => {
        if ((item.type === 'cvi' || item.type === 'variation') && (item.status as string) === 'disputed') {
          disputeCount++;
        }
      });
    });

    // Payment performance (max -30 points)
    if (paymentCount > 0) {
      const paymentPerformance = (onTimePayments / paymentCount) * 100;
      if (paymentPerformance < 50) score -= 30;
      else if (paymentPerformance < 70) score -= 20;
      else if (paymentPerformance < 85) score -= 10;
    }

    // Overdue payments (max -25 points)
    if (overdueCount > 0) {
      if (overdueCount >= 5) score -= 25;
      else if (overdueCount >= 3) score -= 15;
      else score -= 5;
    }

    // Active disputes (max -20 points)
    if (disputeCount > 0) {
      if (disputeCount >= 5) score -= 20;
      else if (disputeCount >= 3) score -= 12;
      else score -= 5;
    }

    // Project progress (max -15 points)
    if (totalContractValue > 0) {
      const progressRatio = certifiedValue / totalContractValue;
      if (progressRatio < 0.3) score -= 15; // Very slow progress
      else if (progressRatio < 0.5) score -= 8;
    }

    // Risk level penalty (max -10 points) — riskLevel is a runtime field not in the typed interface
    const highRiskProjects = projects.filter((p: Project) => (p as any).riskLevel === 'high' || (p as any).riskLevel === 'critical').length;
    if (highRiskProjects > 0) {
      if (highRiskProjects >= 3) score -= 10;
      else score -= 5;
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Get employer by name (for linking projects to employers)
   */
  public getEmployerByName(name: string): Employer | undefined {
    return this.getEmployers().find((e: Employer) => e.name.toLowerCase() === name.toLowerCase());
  }

  /**
   * Get or create employer for a project
   */
  public getOrCreateEmployerForProject(projectName: string, employerName: string): Employer {
    let employer = this.getEmployerByName(employerName);

    if (!employer) {
      // Create default employer
      employer = this.createEmployer({
        name: employerName,
        type: 'main_contractor',
        paymentTerms: '30 days from certification',
        avgDaysToPay: 35,
        paymentReliability: 'good',
        creditworthinessScore: 65,
        creditworthinessGrade: 'B',
        disputeHistory: 0,
        activeProjectsWithClients: 1,
      });
    }

    // createEmployer always returns an Employer when called with valid data
    return employer!;
  }

  /**
   * Get employer data for a project with real financial metrics
   */
  public getEmployerDataForProject(projectId: string): {
    employer: Employer | null;
    relationship: ClientEmployerRelationship | null;
    harshTerms: string[];
    activeDisputes: number;
    totalDisputedValue: number;
  } {
    const project = this.getProject(projectId);
    if (!project) {
      return {
        employer: null,
        relationship: null,
        harshTerms: [],
        activeDisputes: 0,
        totalDisputedValue: 0,
      };
    }

    // Get or create employer
    let employer = project.mainContractorId
      ? this.getEmployer(project.mainContractorId)
      : this.getEmployerByName(project.mainContractor);

    if (!employer && project.mainContractor) {
      employer = this.getOrCreateEmployerForProject(project.name, project.mainContractor);
      // Link employer to project
      this.updateProject(projectId, { mainContractorId: employer.id });
    }

    // Get or create relationship
    let relationship = this.getClientEmployerRelationshipsByProject(projectId);

    // Calculate real financial data
    const apps = this.getInterimApplicationsByProject(projectId);
    const items = this.getContractItemsByProject(projectId);

    const totalApplied = apps.reduce((sum: number, app: InterimApplication) => sum + (app.cumulative_total || 0), 0);
    const totalCertified = apps.reduce((sum: number, app: InterimApplication) =>
      sum + (app.status === 'certified' ? (app.cumulative_total || 0) : 0), 0);
    const totalPaid = apps.reduce((sum: number, app: InterimApplication) => sum + ((app as any).cumulative_paid || 0), 0);
    const outstandingAmount = totalCertified - totalPaid;

    // Calculate overdue amounts
    const now = new Date();
    let overdueAmount = 0;
    let maxDaysOverdue = 0;

    apps.forEach((app: InterimApplication) => {
      if (app.status === 'certified' && app.periodEnd) {
        const dueDate = new Date(new Date(app.periodEnd).getTime() + (project.paymentTerms || 30) * 24 * 60 * 60 * 1000);
        if (dueDate < now) {
          const unpaid = (app.cumulative_total || 0) - ((app as any).cumulative_paid || 0);
          if (unpaid > 0) {
            overdueAmount += unpaid;
            const daysOverdue = Math.floor((now.getTime() - dueDate.getTime()) / (24 * 60 * 60 * 1000));
            maxDaysOverdue = Math.max(maxDaysOverdue, daysOverdue);
          }
        }
      }
    });

    // Count disputes — ContractItem.status doesn't include 'disputed' in its union, but
    // some API paths return it as a runtime value; cast to string for comparison.
    const activeDisputes = items.filter((item: ContractItem) =>
      (item.type === 'cvi' || item.type === 'variation') &&
      ((item.status as string) === 'disputed' || item.status === 'rejected')
    ).length;

    const totalDisputedValue = items
      .filter((item: ContractItem) => (item.type === 'cvi' || item.type === 'variation') && (item.status as string) === 'disputed')
      .reduce((sum: number, item: ContractItem) => sum + (item.value || 0), 0);

    // Determine risk level
    let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
    const riskFactors: string[] = [];

    if (overdueAmount > 50000) {
      riskLevel = 'high';
      riskFactors.push(`£${(overdueAmount / 1000).toFixed(0)}K overdue payment`);
    }
    if (activeDisputes >= 3) {
      riskLevel = riskLevel === 'high' ? 'critical' : 'high';
      riskFactors.push(`${activeDisputes} active disputes`);
    }
    if (employer && employer.paymentReliability === 'poor') {
      riskLevel = riskLevel === 'low' ? 'medium' : 'high';
      riskFactors.push('Poor payment history');
    }
    if (employer && (employer.creditworthinessGrade === 'D' || employer.creditworthinessGrade === 'E')) {
      riskLevel = 'critical';
      riskFactors.push('Low credit rating');
    }

    // Build harsh terms from contract data
    const harshTerms: string[] = [];

    if (project.retentionPercentage > 0) {
      harshTerms.push(`Retention: ${project.retentionPercentage}% (standard release terms apply)`);
    }
    if (project.paymentTerms) {
      harshTerms.push(`Payment terms: ${project.paymentTerms} days from certification`);
    }
    if (project.contractType) {
      harshTerms.push(`Contract type: ${project.contractType}`);
    }
    if (activeDisputes > 0) {
      harshTerms.push(`${activeDisputes} active dispute${activeDisputes > 1 ? 's' : ''} pending resolution`);
    }

    // Create or update relationship if it doesn't exist
    if (!relationship) {
      const nextApp = apps.find((app: InterimApplication) => app.status === 'draft');

      relationship = this.createClientEmployerRelationship({
        clientId: project.clientId,
        clientName: this.getCompany(project.clientId)?.name || 'Client',
        employerId: employer?.id || 'unknown',
        employerName: employer?.name || project.mainContractor,
        projectId: project.id,
        projectName: project.name,
        contractType: project.contractType || 'JCT',
        totalApplied,
        totalCertified,
        totalPaid,
        outstandingAmount,
        overdueAmount,
        daysOverdue: maxDaysOverdue,
        riskLevel,
        riskFactors,
        disputedVariations: activeDisputes,
        overdueRetentions: 0, // Would calculate from retention ledger
        lateCertifications: 0, // Would track certification delays
        missingNotices: 0, // Would track statutory notice compliance
        nextApplicationDue: nextApp?.periodEnd || '',
        nextCertificationDue: '',
        nextPaymentDue: '',
      });
    }

    return {
      employer: employer || null,
      relationship,
      harshTerms,
      activeDisputes,
      totalDisputedValue,
    };
  }

  /**
   * Get rate library data for a company
   */
  public getRateLibraryForCompany(companyId: string): {
    library: RateLibrary | null;
    items: RateLibraryItem[];
    categories: Array<{
      name: string;
      rate_count: number;
      avg_deviation: number;
    }>;
    keyDeviations: Array<{
      item: string;
      your_rate: number;
      market_avg: number;
      deviation_percent: number;
      reason: string;
    }>;
  } {
    // Try to find company's library
    const libraries = Object.values(this.db.rateLibraries as Record<string, RateLibrary> || {});
    let library: RateLibrary | null = libraries.find((lib: RateLibrary) => lib.companyId === companyId) ?? null;

    // If no company library, use a global one or create default
    if (!library) {
      library = libraries.find((lib: RateLibrary) => lib.isPublic) ?? null;
    }

    const items = Object.values(this.db.rateLibraryItems as Record<string, RateLibraryItem> || {}).filter(
      (item: RateLibraryItem) => item.libraryId === library?.id
    );

    // Group by categories and calculate stats
    const categoryMap = new Map<string, { count: number; totalDeviation: number; items: RateLibraryItem[] }>();

    items.forEach((item: RateLibraryItem) => {
      const category = this.getCategoryForItem(item);
      if (!categoryMap.has(category)) {
        categoryMap.set(category, { count: 0, totalDeviation: 0, items: [] });
      }
      const catData = categoryMap.get(category)!;
      catData.count++;
      // varianceFromBenchmark is a runtime field present on some API response paths but not in the typed interface
      catData.totalDeviation += (item as any).varianceFromBenchmark || 0;
      catData.items.push(item);
    });

    const categories = Array.from(categoryMap.entries()).map(([name, data]) => ({
      name,
      rate_count: data.count,
      avg_deviation: data.count > 0 ? data.totalDeviation / data.count : 0,
    }));

    // Find key deviations (items with highest variance from benchmark)
    // ricsBenchmarkCode and varianceFromBenchmark are runtime fields present on some API response paths
    const keyDeviations = items
      .filter((item: RateLibraryItem) => (item as any).ricsBenchmarkCode && Math.abs((item as any).varianceFromBenchmark || 0) > 5)
      .sort((a: RateLibraryItem, b: RateLibraryItem) => Math.abs((b as any).varianceFromBenchmark || 0) - Math.abs((a as any).varianceFromBenchmark || 0))
      .slice(0, 5)
      .map((item: RateLibraryItem) => ({
        item: (item.description ?? item.name) as string,
        your_rate: item.totalRate,
        market_avg: item.totalRate / (1 + ((item as any).varianceFromBenchmark || 0) / 100),
        deviation_percent: ((item as any).varianceFromBenchmark || 0) as number,
        reason: item.notes || 'Variance from RICS benchmark',
      }));

    return {
      library,
      items,
      categories,
      keyDeviations,
    };
  }

  /**
   * Helper to categorize rate library items
   */
  private getCategoryForItem(item: RateLibraryItem): string {
    const code = item.code.toLowerCase();
    if (code.startsWith('a') || code.includes('ground')) return 'Groundworks';
    if (code.startsWith('e') || code.includes('concrete')) return 'Concrete Works';
    if (code.startsWith('g') || code.includes('steel')) return 'Steelwork';
    if (code.startsWith('f') || code.includes('masonry') || code.includes('block')) return 'Blockwork';
    if (code.startsWith('h') || code.includes('roof')) return 'Roofing';
    if (code.startsWith('v') || code.startsWith('w') || code.includes('electric') || code.includes('mechanical')) return 'M&E';
    if (code.startsWith('m') || code.startsWith('k')) return 'Finishes';
    return 'Other';
  }

  // ============================================================================
};
